import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TextInput } from "./TextInput";
import {
  inputWidthClasses as widthClasses,
  type InputWidth,
} from "./inputWidth";

interface AutoCompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Suggestions offered as the user types (and on focus). */
  options: string[];
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  width?: InputWidth | undefined;
  /** Defensive cap on how many matches to render. Defaults to 100. */
  maxOptions?: number | undefined;
  /** Forwarded to the underlying `<input>` — needed for form submission and
   *  test selection by name. */
  name?: string | undefined;
  /**
   * Forwarded to the underlying `<input>`. Address fields often want a junk
   * token (e.g. "nope") to suppress the browser's own address autofill from
   * fighting the suggestion menu.
   */
  autoComplete?: string | undefined;
  /** Forwarded to the underlying `<input>` for test selection. */
  "data-testid"?: string | undefined;
  /** Forwarded to the underlying `<input>` to cap how much can be typed. */
  maxLength?: number | undefined;
}

/**
 * A free-text input with a suggestion menu — the user can type anything, but
 * matching `options` are offered in a dropdown they can pick with the mouse or
 * arrow keys + Enter. Built on TextInput, so it inherits the active control
 * presentation (boxed vs underlined) and FormItem error state.
 */
export function AutoComplete({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  width = "full",
  maxOptions = 100,
  name,
  autoComplete,
  "data-testid": dataTestId,
  maxLength,
}: AutoCompleteProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Case-insensitive substring match. An exact single match is treated as
  // "nothing left to suggest" so the menu doesn't linger once the field already
  // holds a complete option.
  const filtered = useMemo(() => {
    const query = value.trim().toLowerCase();
    const matches = query
      ? options.filter((option) => option.toLowerCase().includes(query))
      : options;
    if (matches.length === 1 && matches[0] === value) return [];
    return matches.slice(0, maxOptions);
  }, [options, value, maxOptions]);

  // Reset the highlight to the top whenever the query changes.
  useEffect(() => {
    setHighlight(0);
  }, [value]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const visible = open && filtered.length > 0;

  function select(option: string) {
    onChange(option);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!visible) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const option = filtered[highlight];
      if (option !== undefined) {
        e.preventDefault();
        select(option);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={`relative ${widthClasses[width]}`}
      onFocus={() => setOpen(true)}
    >
      <TextInput
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        width="full"
        name={name}
        autoComplete={autoComplete}
        data-testid={dataTestId}
        maxLength={maxLength}
      />
      {visible && (
        <ul className="absolute top-full left-0 z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-border bg-card py-1 shadow-floating">
          {filtered.map((option, index) => (
            <AutoCompleteOption_
              key={option}
              option={option}
              highlighted={index === highlight}
              onSelect={() => select(option)}
              onHover={() => setHighlight(index)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function AutoCompleteOption_({
  option,
  highlighted,
  onSelect,
  onHover,
}: {
  option: string;
  highlighted: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);
  return (
    <li
      ref={ref}
      // mousedown (not click) fires before the input's blur, so selection lands
      // before the menu would otherwise close; preventDefault keeps focus.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onHover}
      className={`cursor-pointer px-3 py-1.5 text-sm text-foreground transition-colors ${
        highlighted ? "bg-brand-subtle" : "hover:bg-brand-subtle"
      }`}
    >
      {option}
    </li>
  );
}
